import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * De klasse op `body` die de schil van de pagina wegzet: menu, topbalk,
 * paginakop en statusknoppen. De opmaak staat in het stylesheet; deze hook zet
 * alleen de schakelaar om.
 */
const MAXIMISED_CLASS = 'is-maximised';

/**
 * Een blok dat het hele venster mag innemen.
 *
 * WAAROM GEEN `requestFullscreen()`: het venster dat het slepen opent (de
 * taakbewerker) hangt via een portal aan `#root`, dus buiten het element dat
 * dan fullscreen staat — de gebruiker ziet er niets van. Binnen het venster
 * blijven werkt met elke andere laag van de app samen.
 *
 * De klasse gaat op `body` en niet op het blok zelf: wat weg moet — het menu,
 * de topbalk, de paginakop — staat buiten dit blok, en alleen van bovenaf is
 * dat te bereiken zonder de stapelvolgorde van de hele app te verbouwen.
 *
 * De klasse wordt bij het opruimen altijd verwijderd. Blijft hij staan nadat
 * dit scherm weg is, dan mist de gebruiker zijn menu op elke volgende pagina en
 * is er geen knop meer om hem terug te halen.
 *
 * Het verlaten wordt gemeld (`onExit`), hoe het ook gebeurt — knop, `exit` of
 * Escape — en niet bij het verdwijnen van het scherm zelf. 'Huishoudens
 * inplannen' bestaat alleen op volledig scherm, dus verlaten is daar hetzelfde
 * als terug naar de tabel; die wissel hoort bij de pagina, niet bij deze hook.
 *
 * @param {object} [opties]
 * @param {boolean} [opties.initial=false] - Meteen op volledig scherm beginnen (verzoek BuurtKlusBedrijf voor 'Huishoudens naast de agenda')
 * @param {() => void} [opties.onExit] - Wordt aangeroepen zodra het volledige scherm verlaten wordt
 * @returns {{maximised: boolean, toggle: () => void, exit: () => void}}
 */
const useMaximised = ({ initial = false, onExit } = {}) => {
    const [maximised, setMaximised] = useState(Boolean(initial));

    // In een ref, zodat `exit` en `toggle` stabiel blijven terwijl de aanroeper
    // elke render een nieuwe functie meegeeft.
    /** @type {React.MutableRefObject<(() => void) | undefined>} */
    const onExitRef = useRef(onExit);
    onExitRef.current = onExit;

    const exit = useCallback(() => {
        setMaximised(false);
        if (typeof onExitRef.current === 'function') onExitRef.current();
    }, []);

    useEffect(() => {
        if (maximised) document.body.classList.add(MAXIMISED_CLASS);
        else document.body.classList.remove(MAXIMISED_CLASS);

        return () => document.body.classList.remove(MAXIMISED_CLASS);
    }, [maximised]);

    // Escape is wat iedereen probeert, en zonder deze regel is de enige uitweg
    // de knop — die in een volledig gevuld scherm niet meteen opvalt.
    useEffect(() => {
        if (!maximised) return undefined;

        const onKey = (e) => { if (e.key === 'Escape') exit(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [maximised, exit]);

    return {
        maximised,
        toggle: useCallback(() => (maximised ? exit() : setMaximised(true)), [maximised, exit]),
        exit,
    };
};

export { MAXIMISED_CLASS };
export default useMaximised;
